import { APP_DEEPLINK_SCHEME, WEB_ENROLLMENT_ORIGIN } from './env';
import { ONE_LINK_BASE_URL } from './oneLink';

export type AppsFlyerEnrollmentAttribution = {
    pid: string;
    c: string;
};

export type BuildAppsFlyerEnrollmentOneLinkOptions = {
    /**
     * Web enrollment origin (e.g. https://www.flipgive.com).
     * Native may pass Config.RAISE_RIGHT_APP_URL when set so af_web_dp matches deployed web.
     */
    webEnrollmentBaseUrl?: string;
    /**
     * Path after origin, no leading slash. Default `enroll` → `…/enroll?enrollCode=`.
     * Use `enroll/join-program` for the participant enrollment test variant on web.
     */
    webEnrollPath?: string;
    /**
     * Native: `Config.APP_URL_SCHEME` when set (e.g. CI suffix). Defaults to `APP_DEEPLINK_SCHEME` from env.
     */
    appDeeplinkScheme?: string;
    /**
     * Parameters appended to the web enrollment destination (`af_web_dp`).
     * They are encoded with the destination as a whole when the OneLink is built.
     */
    webEnrollmentQueryParams?: Record<string, string>;
    /**
     * Parameters appended to the app deep-link payload (`deep_link_value` and `af_dp`).
     * Use this when attribution must be available after AppsFlyer opens the installed app.
     */
    appDeepLinkQueryParams?: Record<string, string>;
};

/**
 * Canonical AppsFlyer OneLink URL for participant enrollment (web coordinator tools, web P2P,
 * native P2P share, InviteRedirectScreen). Single implementation avoids web/native query drift.
 */
export function buildAppsFlyerEnrollmentOneLinkUrl(
    enrollmentCode: string,
    attribution: AppsFlyerEnrollmentAttribution,
    options?: BuildAppsFlyerEnrollmentOneLinkOptions
): string {
    const base = options?.webEnrollmentBaseUrl ?? WEB_ENROLLMENT_ORIGIN;
    const path = options?.webEnrollPath ?? 'enroll';
    const scheme = options?.appDeeplinkScheme?.trim() || APP_DEEPLINK_SCHEME;
    const webEnrollmentParams = new URLSearchParams({
        enrollCode: enrollmentCode,
        ...options?.webEnrollmentQueryParams,
    });
    const webDp = `${base}/${path}?${webEnrollmentParams.toString()}`;
    const appDeepLinkParams = new URLSearchParams({
        code: enrollmentCode,
        ...options?.appDeepLinkQueryParams,
    });
    const deepLinkValue = `EnrollmentCode?${appDeepLinkParams.toString()}`;
    const appDp = `${scheme}://EnrollmentCode?${appDeepLinkParams.toString()}`;
    return `${ONE_LINK_BASE_URL}?af_web_dp=${encodeURIComponent(webDp)}&af_xp=custom&pid=${
        attribution.pid
    }&c=${attribution.c}&deep_link_value=${encodeURIComponent(
        deepLinkValue
    )}&af_dp=${encodeURIComponent(appDp)}`;
}
